
export interface Invoice {
  id: string; // Internal ID
  gstin: string;
  invoiceNumber: string;
  invoiceDate: string;
  taxableValue: number;
  taxAmount: number; // This is total tax (IGST+CGST+SGST) for simple logic, or use components
  igst: number;
  cgst: number;
  sgst: number;
  totalAmount: number;
  vendorName: string;
  type: string; // B2B, B2BA, CDNR, etc.
  rcm?: 'Yes' | 'No';
  itcAvailability?: 'Yes' | 'No';

  uploadedMonth?: string; // YYYY-MM
  matchedMonth?: string; // YYYY-MM

  isEdited?: boolean;
  isCarryForward?: boolean;
  isDuplicate?: boolean; // Flag for duplicate uploads
  reconStatus?: string; // Matched, Partial, Unmatched, etc.
  remarks?: string;
  source: 'PR' | '2B';
}

export interface User {
  username: string;
  role: 'admin' | 'manager' | 'user' | 'viewer';
  permissions: string[];
  password?: string; // In a real app, this would be hashed
}

export interface ReconciliationSettings {
  taxTolerance: number; // INR
  taxTolerancePercent: number; // %
  dateTolerance: number; // Days
  fuzzyMatch: boolean; // Legacy, keep for now but new fields will supersede
  ignoreSpecialCharsInInv: boolean;
  matchB2BOnly: boolean;

  // New granular matching controls
  enableGstinMatch: boolean;
  enableFuzzyGstinMatch: boolean; // For Gstin typos
  enableInvoiceNumberMatch: boolean;
  enableFuzzyInvoiceNumberMatch: boolean; // For Invoice Number typos
  enableTaxValueMatch: boolean;
  enableTaxValueToleranceMatch: boolean;
  enableInvoiceDateMatch: boolean;
  enableInvoiceDateToleranceMatch: boolean;

  // New Configuration Options
  multiLineMatch: boolean;
  autoHighlight: boolean;
  duplicateDetection: boolean;
  emptyCellHighlight: boolean;
  caseSensitive: boolean;
  enableUndoRedo: boolean;

  gstinConfig?: {
    enableFuzzy: boolean;
    similarityThreshold: number; // 0-100
    replacements: { from: string; to: string }[];
  };

  invoiceConfig: {
    enableFuzzy: boolean;
    similarityThreshold: number; // 0-100
  };

  // Custom Partial Match Rules
  partialMatchRules: PartialMatchRule[];

  additionalReconSettings: AdditionalReconSettings;
  panMatch: boolean;

  users: User[];

  gspConfig?: {
    clientId: string;
    clientSecret: string;
    subscriptionKey: string;
  };
  storagePath?: string;

  // Encryption Settings
  isEncrypted?: boolean;
  salt?: string;
  masterPasswordSet?: boolean;
  masterPasswordHash?: string;
}

export interface AdditionalReconSettings {
  enabled: boolean;
  gstin: {
    exact: boolean;
    fuzzy: boolean;
  };
  invoiceNumber: {
    exact: boolean;
    fuzzy: boolean; // Represents "Partial" mode in UI
    padding?: boolean; // Enable padding for invoice numbers (default: false removes leading zeros)
  };
  date: {
    exact: boolean;
    tolerance: boolean;
    toleranceUnit?: 'DAYS' | 'MONTHS'; // Unit for date tolerance (default: DAYS)
  };
  taxValue: {
    exact: boolean;
    tolerance: boolean;
    toleranceUnit?: 'INR' | 'PERCENT' | 'BOTH'; // Unit for tax tolerance (default: BOTH checks both)
  };
  invoiceTypeMatching?: 'IGNORE' | 'EXACT' | 'FUZZY'; // Invoice type matching mode (default: IGNORE)
}

// New interface for defining partial match criteria
export interface PartialMatchRule {
  id: string; // Unique ID for the rule
  name: string; // User-friendly name for the rule
  criteria: {
    gstinMatch?: boolean; // True if GSTIN should match (exact or fuzzy based on settings)
    invoiceNumberMatch?: boolean; // True if Invoice Number should match (exact or fuzzy based on settings)
    taxValueMatch?: boolean; // True if Tax Value should match (exact or tolerance based on settings)
    invoiceDateMatch?: boolean; // True if Invoice Date should match (exact or tolerance based on settings)
  };
  resultingStatus: MatchStatus; // The status to set if this rule is met (e.g., PARTIALLY_MATCHED)
  resultingRemarks: string; // Remarks to add if this rule is met
  enabled: boolean; // Whether this rule is active
}

export enum MatchStatus {
  MATCHED = 'Matched',
  PARTIALLY_MATCHED = 'Partially Matched',
  UNMATCHED_PR = 'Unmatched PR',
  UNMATCHED_2B = 'Unmatched 2B',
  RCM = 'RCM',
  INELIGIBLE_ITC = 'Ineligible ITC',
  CARRY_FORWARD = 'Carry Forward',
  AMENDMENT = 'Amendment',
  INTER_BRANCH = 'Inter-branch Match',
  DUPLICATE = 'Duplicate'
}

export interface ReconciliationItem {
  id: string;
  status: MatchStatus;
  remarks: string;
  mismatchDetails: string;

  // Flattened structure for Reports
  prGstin?: string;
  gstrGstin?: string;
  prPartyName?: string;
  gstrPartyName?: string;
  prInvoiceNo?: string;
  gstrInvoiceNo?: string;
  prDate?: string;
  gstrDate?: string;

  dateDiff?: number; // Difference in days

  prIgst: number;
  gstrIgst: number;
  prCgst: number;
  gstrCgst: number;
  prSgst: number;
  gstrSgst: number;

  diffTax: number;

  uploadedMonth?: string;
  matchedMonth?: string;

  prInvoiceRef?: Invoice;
  gstrInvoiceRef?: Invoice;

  // Aggregation Support
  prMatches?: Invoice[];
  gstrMatches?: Invoice[];
}

export interface Stats {
  matchedCount: number;
  partiallyMatchedCount: number;
  unmatchedPRCount: number;
  unmatched2BCount: number;
  rcmCount: number;
  ineligibleItcCount: number;
  interBranchCount: number; // NEW: Inter-branch transaction count
  carryForwardCount: number; // Carry forward items

  // Logic Steps Breakdown
  step1ExactCount: number; // Found by exact Invoice No (includes partials due to tax/date)
  step2FuzzyCount: number; // Found by Fuzzy Logic

  total2BInvoiceCount: number;
  total2BIgst: number;
  total2BCgst: number;
  total2BSgst: number;

  totalPRInvoiceCount: number;
  totalPRIgst: number;
  totalPRCgst: number;
  totalPRSgst: number;

  // NEW: Inter-branch tax totals
  interBranchPrIgst: number; interBranchPrCgst: number; interBranchPrSgst: number;
  interBranch2bIgst: number; interBranch2bCgst: number; interBranch2bSgst: number;

  // Deprecated combined fields (can remove if verified unused elsewhere, but keeping for safety for now or refactoring)
  interBranchIgst: number;
  interBranchCgst: number;
  interBranchSgst: number;

  // Granular Tax Breakdown for Cards (PR vs 2B)
  matchedPrIgst: number; matchedPrCgst: number; matchedPrSgst: number;
  matched2bIgst: number; matched2bCgst: number; matched2bSgst: number;

  partialPrIgst: number; partialPrCgst: number; partialPrSgst: number;
  partial2bIgst: number; partial2bCgst: number; partial2bSgst: number;

  unmatchedPrIgst: number; unmatchedPrCgst: number; unmatchedPrSgst: number; // For Unmatched PR
  unmatched2bIgst: number; unmatched2bCgst: number; unmatched2bSgst: number; // For Unmatched 2B
}

export interface Client {
  id: string;
  name: string;
  gstin: string;
}

export interface DataSet {
  prData: Invoice[];
  gstrData: Invoice[];
  results: ReconciliationItem[];
  stats: Stats;
  lastUpdated: string;
  settings?: ReconciliationSettings;
}

export type ViewState = 'upload' | 'dashboard' | 'reconciliation' | 'settings' | 'data-editor';
